import React from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  styled as MuiStyled,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

type Props = {
  open: boolean;
  message: string;
  quotaExceeded?: boolean;
  onClose: () => void;
};

const QuotaExceededDialog: React.FC<Props> = ({
  open,
  message,
  quotaExceeded,
  onClose,
}) => {
  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <CloseOutlinedIcon
          sx={{
            cursor: 'pointer',
            color: 'text.disabled',
            fontSize: 20,
            position: 'absolute',
            top: 16,
            right: 16,
          }}
          onClick={onClose}
        />

        <svg
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="48"
            height="48"
            rx="16"
            fill="#F3F0FF"
          />
          <path
            d="M16 26.0007C15.6138 26.002 15.2613 25.7807 15.0946 25.4323C14.9278 25.0839 14.9767 24.6706 15.22 24.3707L25.12 14.1707C25.273 13.9941 25.5278 13.947 25.7337 14.0572C25.9397 14.1673 26.042 14.4055 25.98 14.6307L24.06 20.6507C23.9451 20.9582 23.9885 21.3026 24.1761 21.5721C24.3637 21.8416 24.6717 22.0017 25 22.0007H32C32.3863 21.9994 32.7387 22.2206 32.9055 22.569C33.0722 22.9174 33.0233 23.3307 32.78 23.6307L22.88 33.8307C22.7271 34.0072 22.4722 34.0543 22.2663 33.9442C22.0603 33.834 21.9581 33.5959 22.02 33.3707L23.94 27.3507C24.0549 27.0431 24.0115 26.6987 23.8239 26.4292C23.6363 26.1598 23.3283 25.9996 23 26.0007H16"
            stroke="#4440CC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <Typography
          variant="h6"
          fontWeight={700}
          mt={2}
          mb={1}
        >
          {quotaExceeded
            ? 'Your current plan limit has been reached'
            : 'Error'}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          mb={2.5}
        >
          {quotaExceeded
            ? 'Upgrade your plan to continue creating and publishing more forms.'
            : message}
        </Typography>

        {quotaExceeded && (
          <UsageBox>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="body2" fontWeight={500}>
                Forms
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                color="error.main"
              >
                Limit Reached
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                height: 6,
                borderRadius: 4,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'error.main',
                  borderRadius: 4,
                },
              }}
            />
          </UsageBox>
        )}

        <UpgradeButton
          variant="contained"
          fullWidth
          onClick={() =>
            window.open(
              'https://cs.fabbuilder.com/settings/subscriptions',
              '_blank',
              'noopener,noreferrer',
            )
          }
        >
          Upgrade Plan
        </UpgradeButton>

        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{
            cursor: 'pointer',
            mt: 1.5,
            '&:hover': { color: 'text.primary' },
          }}
          onClick={onClose}
        >
          Maybe Later
        </Typography>
      </Container>
    </Overlay>
  );
};

export default QuotaExceededDialog;

const Overlay = MuiStyled(Box)({
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 10000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Container = MuiStyled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  padding: '28px 28px 24px',
  width: 400,
  maxWidth: '92vw',
  background:
    theme.palette.mode === 'light'
      ? 'linear-gradient(116.56deg, #FFFFFF 0.51%, #FCE9FF 100%)'
      : 'linear-gradient(116.56deg, #2B2B2B 0.51%, #3A1F3D 100%)',
  boxShadow: '0 8px 40px rgba(0,0,0,0.16)',
}));

const UsageBox = MuiStyled(Box)(({ theme }) => ({
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  padding: '12px 14px',
  marginBottom: 20,
  backgroundColor: theme.palette.background.paper,
}));

const UpgradeButton = MuiStyled(Button)({
  height: 36,
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  outline: 'none',
  color: '#fff !important',
  backgroundColor: '#4440CC',
  '&:hover, &:focus, &:focus-visible, &:active': {
    boxShadow: 'none',
    outline: 'none',
    color: '#fff !important',
    backgroundColor: '#3333AA',
  },
}) as typeof Button;
